import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { updateProfile } from '../../../api';
import { getApiErrorMessage } from '../../../utils/apiErrorUtils';
import { useEnterprise } from '../../../contexts/EnterpriseContext';
import './index.css';
import './index.mobile.css';

const INFO_ICON = `${process.env.PUBLIC_URL}/image/ff_info_circle.svg`;

export default function CompanyInfo() {
  const { t } = useTranslation();
  const { enterprise, setEnterprise } = useEnterprise() || {};

  const [form, setForm] = useState({
    companyName: '',
    contactName: '',
    contactTitle: '',
    email: '',
    phone: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!enterprise) return;
    setForm({
      companyName: enterprise.companyName || '',
      contactName: enterprise.nickname || '',
      contactTitle: enterprise.contactTitle || '',
      email: enterprise.loginEmail || enterprise.email || '',
      phone: enterprise.contactPhone || '',
    });
  }, [enterprise]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const res = await updateProfile({
        companyName: form.companyName,
        contactName: form.contactName,
        contactTitle: form.contactTitle,
        loginEmail: form.email,
        contactPhone: form.phone,
      });
      setEnterprise((prev) => ({ ...prev, ...res.result }));
    } catch (err) {
      setSaveError(getApiErrorMessage(err.message, t, 'ff.companyInfo.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!enterprise) return;
    setForm({
      companyName: enterprise.companyName || '',
      contactName: enterprise.nickname || '',
      contactTitle: enterprise.contactTitle || '',
      email: enterprise.loginEmail || enterprise.email || '',
      phone: enterprise.contactPhone || '',
    });
    setSaveError('');
  };

  return (
    <div className="ffci-page">
      {/* 企业信息维护 */}
      <div className="ffci-card">
        <div className="ffci-section-title">
          <span>{t('ff.companyInfo.title')}</span>
          <span className="ffci-section-dot" />
        </div>

        {/* 账号行 */}
        <div className="ffci-account-row-container">
          <div className="ffci-account-row">
            <span className="ffci-account-label">{t('ff.companyInfo.accountLabel')}</span>
            <span className="ffci-account-code">{enterprise?.username || '-'}</span>
            <span className="ffci-account-readonly">{t('ff.companyInfo.accountReadonly')}</span>
          </div>
          <p className="ffci-account-note">{t('ff.companyInfo.accountNote')}</p>
        </div>

        {/* 企业名称 */}
        <div className="ffci-field">
          <label className="ffci-field-label">
            <span className="ffci-required">*</span>
            {t('ff.companyInfo.companyName')}
          </label>
          <input
            className="ffci-input ffci-input--readonly"
            value={form.companyName}
            readOnly
          />
          <div className="ffci-field-hint">
            <img src={INFO_ICON} alt="" className="ffci-hint-icon" />
            <span>{t('ff.companyInfo.accountHint')}</span>
          </div>
        </div>

        {/* 联系人 + 岗位 */}
        <div className="ffci-row-2col">
          <div className="ffci-field">
            <label className="ffci-field-label">
              <span className="ffci-required">*</span>
              {t('ff.companyInfo.contactName')}
            </label>
            <input
              className="ffci-input"
              value={form.contactName}
              onChange={handleChange('contactName')}
            />
          </div>
          <div className="ffci-field">
            <label className="ffci-field-label">
              <span className="ffci-required">*</span>
              {t('ff.companyInfo.contactTitle')}
            </label>
            <input
              className="ffci-input"
              value={form.contactTitle}
              onChange={handleChange('contactTitle')}
            />
          </div>
        </div>

        {/* 邮箱 + 手机 */}
        <div className="ffci-row-2col">
          <div className="ffci-field">
            <label className="ffci-field-label">
              <span className="ffci-required">*</span>
              {t('ff.companyInfo.email')}
            </label>
            <input className="ffci-input" value={form.email} onChange={handleChange('email')} />
            <div className="ffci-field-hint">
              <img src={INFO_ICON} alt="" className="ffci-hint-icon" />
              <span>{t('ff.companyInfo.emailHint')}</span>
            </div>
          </div>
          <div className="ffci-field">
            <label className="ffci-field-label">
              <span className="ffci-required">*</span>
              {t('ff.companyInfo.phone')}
            </label>
            <input className="ffci-input" value={form.phone} onChange={handleChange('phone')} />
          </div>
        </div>

        {saveError && <p style={{ color: '#f54336', marginBottom: 12 }}>{saveError}</p>}

        <div className="ffci-actions">
          <button className="ffci-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? t('ff.companyInfo.saving') : t('ff.companyInfo.save')}
          </button>
          <button className="ffci-btn-cancel" onClick={handleCancel}>
            {t('ff.companyInfo.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
